import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  OpenInNew as ViewIcon,
  PrivacyTip as ConsentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useSWR, { mutate as globalMutate } from 'swr';
import { z } from 'zod';
import { consent, type ConsentRecord } from '@/api/clinical';
import { useAuth } from '@/auth/AuthContext';
import { DataTable } from '@/components/DataTable';
import FhirBundleModal from '@/components/fhir/FhirBundleModal';
import { FieldRow } from '@/components/FieldRow';
import { Pagination } from '@/components/Pagination';
import { StatusPill } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime, utcStamp } from '@/utils/format';

/**
 * Consent artefact list + raise-request form. The list is keyed on
 * `healthId` (ABHA address); the operator picks one before listing or
 * raising. We use a single dialog with RHF + Zod for the raise form
 * because it's not multi-step — submit immediately.
 *
 * `hiTypes` and `purpose` are ABDM-canonical vocabularies; we render
 * them as multi-select Chip groups so the operator can't typo into a
 * 400 from the gateway.
 */

const PAGE_SIZE = 25;

const HI_TYPES = [
  'OPConsultation', 'Prescription', 'DiagnosticReport',
  'DischargeSummary', 'ImmunizationRecord', 'HealthDocumentRecord', 'WellnessRecord',
] as const;

const PURPOSES = [
  { value: 'CAREMGT', label: 'Care management' },
  { value: 'BTG',     label: 'Break-the-glass' },
  { value: 'PUBHLTH', label: 'Public health' },
  { value: 'HPAYMT',  label: 'Healthcare payment' },
  { value: 'DSRCH',   label: 'Disease research' },
  { value: 'PATRQT',  label: 'Self-requested' },
] as const;

export function ConsentPage() {
  const { claims } = useAuth();
  const tenantId = (claims as any)?.tenantId ?? '';
  const toast = useToast();

  const [healthId, setHealthId] = useState('');
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [raising, setRaising] = useState(false);
  const [picked, setPicked] = useState<ConsentRecord | null>(null);

  // FHIR viewer state
  const [fhirOpen, setFhirOpen] = useState(false);
  const [fhirBundles, setFhirBundles] = useState<any[]>([]);
  const [fhirLoading, setFhirLoading] = useState(false);
  const [fhirIndex, setFhirIndex] = useState(0);
  const [fhirArtifactId, setFhirArtifactId] = useState<string | undefined>();
  const [retriggering, setRetriggering] = useState(false);

  const loadFhirBundles = async (artifactId: string) => {
    setFhirLoading(true);
    try {
      const data = await consent.fhir(artifactId, tenantId);
      setFhirBundles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load records', (err as Error).message);
      setFhirOpen(false);
    } finally {
      setFhirLoading(false);
    }
  };

  const handleViewRecords = async (rec: ConsentRecord, e: React.MouseEvent) => {
    e.stopPropagation(); // don't open detail dialog
    const artifactId = rec.consentArtefactId;
    if (!artifactId) {
      toast.error('No artefact', 'This consent has no artefact ID yet. Wait for ABDM to send it.');
      return;
    }
    setFhirBundles([]);
    setFhirIndex(0);
    setFhirArtifactId(artifactId);
    setFhirOpen(true);
    await loadFhirBundles(artifactId);
  };

  const handleRetrigger = async () => {
    if (!fhirArtifactId) return;
    setRetriggering(true);
    try {
      await consent.retrigger(fhirArtifactId);
      toast.success('Re-fetch triggered', 'The HIP has been asked to resend the records. Refreshing in 30 seconds…');
      // Poll after 30 s — the HIP usually responds within 10-30 s
      setTimeout(async () => {
        if (fhirArtifactId) await loadFhirBundles(fhirArtifactId);
        setRetriggering(false);
      }, 30_000);
    } catch (err) {
      toast.error('Re-fetch failed', (err as Error).message);
      setRetriggering(false);
    }
  };

  const swrKey = healthId && tenantId ? ['consents', healthId, tenantId, page, statusFilter] : null;
  const { data, error, isLoading } = useSWR(
    swrKey,
    () => consent.list({
      healthId,
      tenantId,
      page,
      size: PAGE_SIZE,
      status: statusFilter || undefined,
    }),
  );
  const refresh = () => globalMutate((k) => Array.isArray(k) && k[0] === 'consents');

  return (
    <PageShell
      kicker="TENANT · CONSENT"
      title="Consent"
      subtitle="Raise consent requests, list the patient's artefacts, and inspect responses. ABDM-canonical vocabularies guard the form so 400s never reach the gateway."
      width="wide"
      actions={
        <Stack direction="row" spacing={1}>
          <IconButton onClick={refresh} aria-label="refresh"><RefreshIcon fontSize="small" /></IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRaising(true)}
            disabled={!healthId}
          >
            Raise request
          </Button>
        </Stack>
      }
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          label="ABHA address"
          placeholder="patient@abdm"
          value={healthId}
          onChange={(e) => { setHealthId(e.target.value); setPage(0); }}
          sx={{ minWidth: 280 }}
          helperText={!healthId ? 'Required to list this patient\'s consents' : ' '}
        />
        <TextField
          size="small"
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="REQUESTED">REQUESTED</MenuItem>
          <MenuItem value="GRANTED">GRANTED</MenuItem>
          <MenuItem value="DENIED">DENIED</MenuItem>
          <MenuItem value="REVOKED">REVOKED</MenuItem>
          <MenuItem value="EXPIRED">EXPIRED</MenuItem>
        </TextField>
      </Stack>

      {!healthId ? (
        <Alert severity="info">Enter an ABHA address above to list this patient's consents.</Alert>
      ) : (
        <>
          <DataTable<ConsentRecord>
            rows={data?.content}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error ? { message: (error as Error).message } : null}
            empty={{
              icon: ConsentIcon,
              title: 'No consents for this patient yet.',
              description: 'Raise the first request to start the consent flow.',
              action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRaising(true)}>Raise request</Button>,
            }}
            onRowClick={(r) => setPicked(r)}
            columns={[
              {
                header: 'When',
                cell: (r) => <Typography variant="caption" color="text.secondary" title={utcStamp(r.createdAt)}>{relativeTime(r.createdAt)}</Typography>,
                width: 130,
              },
              {
                header: 'Status',
                cell: (r) => {
                  const tone = r.status === 'GRANTED' ? 'success'
                    : r.status === 'REQUESTED' ? 'info'
                    : r.status === 'DENIED' || r.status === 'REVOKED' ? 'danger'
                    : 'mono';
                  return <StatusPill tone={tone}>{r.status}</StatusPill>;
                },
                width: 130,
              },
              {
                header: 'HI types',
                cell: (r) => (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {r.hiTypes.slice(0, 3).map((t) => (
                      <Chip key={t} label={t} size="small" sx={{ fontSize: 10, height: 20 }} />
                    ))}
                    {r.hiTypes.length > 3 && <Typography variant="caption" color="text.tertiary">+{r.hiTypes.length - 3}</Typography>}
                  </Stack>
                ),
              },
              {
                header: 'Purpose',
                cell: (r) => <Typography variant="caption">{r.purpose ?? '—'}</Typography>,
                width: 130,
              },
              {
                header: 'Window',
                cell: (r) => (
                  <Typography variant="caption" color="text.secondary">
                    {r.permissionFrom ? r.permissionFrom.slice(0, 10) : '—'} → {r.permissionTo ? r.permissionTo.slice(0, 10) : '—'}
                  </Typography>
                ),
                width: 200,
              },
              {
                header: '',
                cell: (r) => r.status === 'GRANTED' ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon fontSize="small" />}
                    onClick={(e) => handleViewRecords(r, e)}
                    sx={{ fontSize: 11, py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                  >
                    View records
                  </Button>
                ) : null,
                width: 130,
              },
            ]}
          />
          <Pagination page={data} onChange={setPage} />
        </>
      )}

      <RaiseDialog
        open={raising}
        defaultHealthId={healthId}
        onClose={() => setRaising(false)}
        onRaised={() => { setRaising(false); refresh(); }}
      />
      <ConsentDialog rec={picked} onClose={() => setPicked(null)} />

      {/* FHIR Bundle Viewer */}
      <FhirBundleModal
        open={fhirOpen}
        onClose={() => { setFhirOpen(false); setRetriggering(false); }}
        bundles={fhirBundles}
        currentIndex={fhirIndex}
        onIndexChange={setFhirIndex}
        loading={fhirLoading}
        onRetrigger={handleRetrigger}
        retriggering={retriggering}
      />
    </PageShell>
  );
}

// -------------------- Raise dialog --------------------

const today = new Date().toISOString().slice(0, 10);
const sixMonthsOut = (() => { const d = new Date(); d.setMonth(d.getMonth() + 6); return d.toISOString().slice(0, 10); })();

const raiseSchema = z.object({
  healthId: z.string().min(3, 'ABHA address is required'),
  purpose: z.enum(['CAREMGT', 'BTG', 'PUBHLTH', 'HPAYMT', 'DSRCH', 'PATRQT']),
  purposeText: z.string().optional(),
  hiTypes: z.array(z.string()).min(1, 'Pick at least one HI type'),
  permissionFrom: z.string().min(1),
  permissionTo: z.string().min(1),
  expiry: z.string().min(1),
  hipId: z.string().optional(),
}).refine((v) => v.permissionFrom <= v.permissionTo, {
  path: ['permissionTo'],
  message: 'To-date must be on or after From-date',
});
type RaiseForm = z.infer<typeof raiseSchema>;

function RaiseDialog({
  open,
  defaultHealthId,
  onClose,
  onRaised,
}: {
  open: boolean;
  defaultHealthId: string;
  onClose: () => void;
  onRaised: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<RaiseForm>({
    defaultValues: {
      healthId: defaultHealthId,
      purpose: 'CAREMGT',
      hiTypes: ['OPConsultation'],
      permissionFrom: today,
      permissionTo: today,
      expiry: sixMonthsOut,
    },
  });
  const selectedHiTypes = watch('hiTypes');

  useEffect(() => {
    if (open) {
      reset({
        healthId: defaultHealthId,
        purpose: 'CAREMGT',
        hiTypes: ['OPConsultation'],
        permissionFrom: today,
        permissionTo: today,
        expiry: sixMonthsOut,
        purposeText: '',
        hipId: '',
      });
    }
  }, [open, defaultHealthId, reset]);

  const toggleHiType = (t: string) => {
    const next = selectedHiTypes.includes(t)
      ? selectedHiTypes.filter((x) => x !== t)
      : [...selectedHiTypes, t];
    setValue('hiTypes', next, { shouldValidate: true });
  };

  const onSubmit = async (form: RaiseForm) => {
    try {
      // ABDM expects ISO instants for the permission window — pad date-only inputs.
      const pad = (d: string) => d.length === 10 ? `${d}T00:00:00.000Z` : d;
      await consent.raise({
        ...form,
        permissionFrom: pad(form.permissionFrom),
        permissionTo:   pad(form.permissionTo),
        expiry:         pad(form.expiry),
      });
      toast.success('Consent request raised', `Patient must approve in their ABHA app.`);
      onRaised();
    } catch (e) {
      toast.error('Raise failed', (e as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Raise consent request
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="raise-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2.5}>
            <TextField label="ABHA address" fullWidth {...register('healthId')} error={!!errors.healthId} helperText={errors.healthId?.message} />
            <Controller
              name="purpose"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Purpose" select fullWidth>
                  {PURPOSES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label} <Typography component="span" variant="caption" color="text.tertiary" sx={{ ml: 1 }}>{p.value}</Typography></MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField label="Purpose note (optional)" multiline minRows={2} fullWidth {...register('purposeText')} />
            <Stack spacing={1}>
              <Typography variant="overline" color="text.tertiary">HI types</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {HI_TYPES.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => toggleHiType(t)}
                    color={selectedHiTypes.includes(t) ? 'primary' : 'default'}
                    sx={{
                      bgcolor: selectedHiTypes.includes(t) ? theme.palette.brand.lighter : theme.palette.surface.sunken,
                      color: selectedHiTypes.includes(t) ? theme.palette.brand.dark : 'text.secondary',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
              {errors.hiTypes && <Typography variant="caption" color="error">{errors.hiTypes.message as string}</Typography>}
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField type="date" label="Permission from" InputLabelProps={{ shrink: true }} {...register('permissionFrom')} error={!!errors.permissionFrom} helperText={errors.permissionFrom?.message} />
              <TextField type="date" label="Permission to" InputLabelProps={{ shrink: true }} {...register('permissionTo')} error={!!errors.permissionTo} helperText={errors.permissionTo?.message} />
            </Box>
            <TextField type="date" label="Artefact expiry" InputLabelProps={{ shrink: true }} {...register('expiry')} error={!!errors.expiry} helperText={errors.expiry?.message ?? 'Defaults to six months out.'} />
            <TextField label="HIP ID (optional)" {...register('hipId')} helperText="Empty = ABDM resolves from the patient's available HIPs." />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          type="submit"
          form="raise-form"
          variant="contained"
          disabled={isSubmitting}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          {isSubmitting ? 'Raising…' : 'Raise request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -------------------- Detail dialog --------------------

function ConsentDialog({ rec, onClose }: { rec: ConsentRecord | null; onClose: () => void }) {
  const theme = useTheme();
  const tone = rec?.status === 'GRANTED' ? 'success'
    : rec?.status === 'REQUESTED' ? 'info'
    : rec?.status === 'DENIED' || rec?.status === 'REVOKED' ? 'danger'
    : 'mono';

  return (
    <Dialog open={!!rec} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Consent record
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {rec && (
          <>
            <FieldRow label="Status" value={<StatusPill tone={tone}>{rec.status}</StatusPill>} />
            <FieldRow label="ABHA address" value={rec.abhaAddress} mono />
            <FieldRow label="Request id" value={rec.consentRequestId ? redactMiddle(rec.consentRequestId, 6, 6) : '—'} mono />
            <FieldRow label="Artefact id" value={rec.consentArtefactId ? redactMiddle(rec.consentArtefactId, 6, 6) : '—'} mono />
            <FieldRow label="Purpose" value={rec.purpose ?? '—'} />
            <FieldRow
              label="Requested HI types"
              value={
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {rec.hiTypes.map((t) => <Chip key={t} label={t} size="small" sx={{ fontSize: 10, height: 20 }} />)}
                </Stack>
              }
            />
            {rec.grantedhiTypes && rec.grantedhiTypes.length > 0 && (
              <FieldRow
                label="Granted HI types"
                value={
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {rec.grantedhiTypes.map((t) => <Chip key={t} label={t} size="small" color="success" sx={{ fontSize: 10, height: 20 }} />)}
                  </Stack>
                }
              />
            )}
            <FieldRow label="Requested window" value={`${rec.permissionFrom?.slice(0, 10) ?? '—'} → ${rec.permissionTo?.slice(0, 10) ?? '—'}`} />
            {rec.approvedFrom && <FieldRow label="Approved window" value={`${rec.approvedFrom.slice(0, 10)} → ${rec.approvedTo?.slice(0, 10) ?? '—'}`} />}
            <FieldRow label="Expiry" value={rec.expiry?.slice(0, 10) ?? '—'} />
            <FieldRow label="Raised" value={utcStamp(rec.createdAt)} />
            {rec.approvedAt && <FieldRow label="Approved at" value={utcStamp(rec.approvedAt)} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
